import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useApprovalsQueue, useHrAction } from "../../lib/api";
import { fmtNumber, fmtValue } from "../../lib/format";
import PositionApprovals from "./PositionApprovals";
import { SectionCard, pillClass, useDateFormat } from "./shared";

/** Leave and expense decisions waiting on the signed-in person — or on anybody, for HR (WP-23.1).
 *
 * Its own screen because it is the one thing on the lifecycle that somebody comes to *do* rather than read, and
 * it used to sit below an org chart and six months of payroll that had to load first. */
export default function Approvals({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useApprovalsQueue(company || undefined);
  const act = useHrAction();
  const fmtDate = useDateFormat();
  const [note, setNote] = useState<Record<string, string>>({});
  const [done, setDone] = useState<Record<string, string>>({});

  const onAct = (kind: "leave" | "expense", name: string, action: "approve" | "reject") =>
    act.mutate({ kind, name, action, note: note[name] },
      { onSuccess: (r) => setDone((d) => ({ ...d, [name]: r.status ?? r.approval_status ?? action })) });

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const total = d.leaves.length + d.expense_claims.length;

  const decision = (name: string, kind: "leave" | "expense") =>
    done[name] ? <span className="text-xs font-semibold text-status-green">{t(`hr.acted.${done[name]}`, done[name])}</span> : (
      <div className="flex items-center justify-end gap-1">
        {kind === "leave" && (
          <input value={note[name] ?? ""} onChange={(e) => setNote((n) => ({ ...n, [name]: e.target.value }))}
            placeholder={t("hr.note")} className="w-32 rounded border border-brand-grey-light px-2 py-1 text-xs" />
        )}
        <button onClick={() => onAct(kind, name, "approve")} disabled={act.isPending} data-hr-approve
          className="rounded bg-status-green px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">{t("hr.approve")}</button>
        <button onClick={() => onAct(kind, name, "reject")} disabled={act.isPending}
          className="rounded border border-status-red px-2 py-1 text-xs font-semibold text-status-red disabled:opacity-50">{t("hr.reject")}</button>
      </div>
    );

  return (
    <div className="mt-4 space-y-4" data-hr-approvals>
      <div className="flex justify-end"><FreshnessStamp lastUpdated={d.as_of} /></div>
      <PositionApprovals company={company} />
      <SectionCard title={t("hr.approvals")} aside={d.mine_only ? t("hr.approvalsMine") : t("hr.approvalsAll")}>
        {total === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("hr.noApprovals")}</div> : (
          <table className="w-full text-sm">
            <tbody>
              {d.leaves.map((l) => (
                <tr key={l.name} className="border-t border-brand-grey-light">
                  <td className="px-4 py-2"><span className={pillClass("Open")}>{t("hr.leave")}</span></td>
                  <td className="px-4 py-2">
                    <div className="font-semibold">{l.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">
                      <CompanyLogo company={l.company} size={12} className="mr-1 align-middle" />
                      {l.leave_type} · {fmtDate(l.from_date)} → {fmtDate(l.to_date)} · {fmtNumber(l.total_leave_days, lng, 1)} {t("units.days")}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-brand-grey">{l.description}</td>
                  <td className="px-4 py-2 text-right">{decision(l.name, "leave")}</td>
                </tr>
              ))}
              {d.expense_claims.map((c) => (
                <tr key={c.name} className="border-t border-brand-grey-light">
                  <td className="px-4 py-2"><span className={pillClass("Draft")}>{t("hr.expense")}</span></td>
                  <td className="px-4 py-2">
                    <div className="font-semibold">{c.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">
                      <CompanyLogo company={c.company} size={12} className="mr-1 align-middle" />
                      {fmtDate(c.posting_date)} · {fmtValue(c.total_claimed_amount, "CDF", lng)}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-brand-grey">{c.remark}</td>
                  <td className="px-4 py-2 text-right">{decision(c.name, "expense")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {act.isError && <div className="border-t border-brand-grey-light p-3"><ErrorBox error={act.error} /></div>}
      </SectionCard>
    </div>
  );
}
